import { useQuery } from '@tanstack/react-query'
import { Receipt } from 'lucide-react'
import { useState } from 'react'

import type { Payment } from './types'
import { formatDate, formatSoles, invoiceNumber } from './types'
import { InvoiceDialog } from './InvoiceDialog'
import { api } from '../../api/client'
import { Badge, Card, Skeleton } from '../../ui'

/** Cómo se llama cada estado cuando lo lee quien pagó, no quien programó. */
const ESTADO: Record<Payment['status'], { label: string; tone: 'positive' | 'warning' | 'danger' | 'neutral' }> = {
  paid: { label: 'Cobrado', tone: 'positive' },
  authorized: { label: 'Autorizado', tone: 'positive' },
  pending: { label: 'En curso', tone: 'warning' },
  failed: { label: 'Rechazado', tone: 'danger' },
  refunded: { label: 'Devuelto', tone: 'neutral' },
  canceled: { label: 'Cancelado', tone: 'neutral' },
}

const MEDIO: Record<Payment['method'], string> = {
  card: 'Tarjeta',
  yape: 'Yape',
  transfer: 'Transferencia',
  cash: 'Efectivo',
  manual: 'Registrado a mano',
}

/**
 * El motivo del rechazo, dicho en español.
 *
 * Los códigos son vocabulario nuestro —`ChargeResult` los normaliza antes de
 * guardarlos, no vienen crudos de la pasarela—, así que traducirlos aquí es
 * seguro. Si aparece uno sin traducir se muestra tal cual: un código raro es
 * feo, pero es lo único con lo que soporte puede reconocer el cargo.
 */
const MOTIVO: Record<string, string> = {
  insufficient_funds: 'Sin fondos suficientes',
  card_canceled: 'Tarjeta cancelada',
  card_expired: 'Tarjeta vencida',
  card_declined: 'El banco no lo autorizó',
  invalid_token: 'La tarjeta guardada ya no vale',
  gateway_error: 'Fallo de la pasarela',
}

/**
 * Los cobros del estudio.
 *
 * Se enseñan los cobros y no sólo los comprobantes porque el comprobante llega
 * detrás —lo emite ChambaTotal, y SUNAT tiene que confirmarlo—, mientras que el
 * cobro siempre está: si sólo listáramos comprobantes, un estudio al que
 * acabamos de cobrar vería una pantalla vacía.
 */
export function PaymentHistory() {
  const [comprobante, setComprobante] = useState<string | null>(null)

  const payments = useQuery({
    queryKey: ['billing', 'payments'],
    queryFn: () => api.get<{ data: Payment[] }>('/billing/payments'),
  })

  return (
    <div className="flex flex-col gap-3">
      <h2 className="text-subhead font-medium text-ink">Cobros</h2>

      {payments.isPending ? (
        <Skeleton className="h-24 w-full rounded-card" />
      ) : payments.error ? (
        <Card className="p-5">
          <p className="text-body text-ink-muted">
            No se pudo cargar el historial de cobros. Tu plan y tu uso, que salen arriba, no se ven
            afectados.
          </p>
        </Card>
      ) : payments.data.data.length === 0 ? (
        <Card className="p-5">
          <p className="text-body text-ink-muted">
            Todavía no hay ningún cobro. El primero aparece aquí en cuanto se hace.
          </p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <ul className="divide-y divide-line">
            {payments.data.data.map((payment) => {
              const estado = ESTADO[payment.status]
              /* Sólo se abre lo que SUNAT aceptó: uno en camino o rechazado
                 acabaría en la contabilidad de alguien como si valiera. */
              const numero =
                payment.invoice_id !== null && payment.sunat_status === 'accepted'
                  ? invoiceNumber(payment)
                  : null

              return (
                <li
                  key={payment.id}
                  className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 px-5 py-4"
                >
                  <div className="min-w-0">
                    <p className="text-label font-medium text-ink" data-numeric>
                      {formatDate(payment.settled_at ?? payment.attempted_at)}
                    </p>
                    <p className="mt-0.5 text-caption text-ink-subtle">
                      {MEDIO[payment.method]}
                      {payment.error_code !== null &&
                        ` · ${MOTIVO[payment.error_code] ?? payment.error_code}`}
                    </p>
                  </div>

                  <div className="flex items-center gap-3">
                    {numero !== null ? (
                      /* El número deja de ser texto muerto: es lo que el estudio
                         busca cuando su contador le pide «la factura». */
                      <button
                        type="button"
                        onClick={() => {
                          setComprobante(payment.invoice_id)
                        }}
                        className="flex items-center gap-1.5 rounded text-caption text-accent underline-offset-2 hover:underline"
                      >
                        <Receipt size={14} strokeWidth={1.75} aria-hidden />
                        <span data-numeric>{numero}</span>
                        {/* Uno de la casa en pruebas no existe ante SUNAT, y quien
                            lo vea tiene que saberlo antes de abrirlo. */}
                        {payment.invoice_environment === 'beta' && (
                          <Badge tone="warning">de prueba</Badge>
                        )}
                      </button>
                    ) : (
                      payment.status === 'paid' && (
                        <span className="text-caption text-ink-subtle">
                          {payment.sunat_status === 'rejected'
                            ? 'Comprobante en revisión'
                            : 'Comprobante en camino'}
                        </span>
                      )
                    )}

                    <Badge tone={estado.tone}>{estado.label}</Badge>

                    <span className="text-label font-medium text-ink tabular-nums" data-numeric>
                      {formatSoles(payment.amount_cents)}
                    </span>
                  </div>
                </li>
              )
            })}
          </ul>
        </Card>
      )}

      {/*
        El aviso sólo mientras haya cobros y NINGÚN comprobante emitido.

        Prometemos comprobante electrónico en la web pública, y quien venga a
        buscarlo merece saber que llega detrás del cobro en vez de suponer que se
        perdió. En cuanto hay uno, sobra.
      */}
      {payments.data !== undefined &&
        payments.data.data.some((payment) => payment.status === 'paid') &&
        payments.data.data.every((payment) => payment.sunat_status !== 'accepted') && (
          <p className="text-caption text-ink-subtle">
            Tu comprobante electrónico —factura o boleta— aparece aquí en cuanto se emite. Si
            necesitas uno y no lo ves, escríbenos.
          </p>
        )}

      <InvoiceDialog
        invoiceId={comprobante}
        onClose={() => {
          setComprobante(null)
        }}
      />
    </div>
  )
}
